import { callTypes } from '../../../../kaylee/CallType';
import { Sutl } from '../../../../sutl';
import { StateCollection } from './State';
export class CallsState extends StateCollection {
    constructor(callParticipants, logFactory) {
        super();
        this.callParticipants = callParticipants;
        this.log = logFactory.channel('CallsState');
    }
    // --------------------------------------------------------------------------------------------
    // Methods for drivers to update the state.
    // --------------------------------------------------------------------------------------------
    // Add missing properties to a call. This doesn't update the state -- it returns the filled-in
    // call. Drivers may want to use this to generate the initial calls object to return from the
    // getCalls method during initialization.
    addMissing(call) {
        const timestamp = Date.now();
        if (call.callId === undefined)
            call.callId = Sutl.generateFirebaseId();
        if (call.callState === undefined)
            call.callState = 'disconnected';
        if (call.callType === undefined)
            call.callType = 'video';
        if (call.direction === undefined)
            call.direction = 'outgoing';
        if (call.callState === 'disconnected' && call.end === undefined)
            call.end = timestamp;
        if (call.start === undefined)
            call.start = timestamp;
        return call;
    }
    // Answer an incoming call.
    answer(key, extras) {
        const call = this.values[key];
        let newCall;
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnected') {
            this.log.error('answering disconnected call');
            newCall = Object.assign({}, call); // shallow copy is sufficient for this change
            this.initializeCall(newCall, undefined, 'incoming', 'connected', extras);
        }
        else if (call.callState === 'ringing' && call.direction === 'incoming') {
            newCall = Object.assign(Object.assign({}, call), { callState: 'connected' }); // shallow copy is sufficient for this
            this.internalUpdateCall(newCall, extras, newCall);
        }
        else if (call.callState !== 'connected') {
            this.log.error(`answering call with invalid callState = ${call.callState}`);
            newCall = Object.assign(Object.assign({}, call), { callState: 'connected' }); // shallow copy is sufficient for this
            this.internalUpdateCall(newCall, extras, newCall);
        }
        else {
            newCall = this.internalUpdateCall(call, extras);
        }
        if (newCall !== undefined) {
            this.log.debug(`answer: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    call(key, arg1, arg2, arg3) {
        let callType;
        let extras;
        const callId = arg2;
        if (typeof arg1 === 'string') {
            callType = arg1;
        }
        if (arg1 !== undefined && typeof arg1 !== 'string') {
            extras = arg1;
        }
        else if (arg3 !== undefined && typeof arg3 !== 'string') {
            extras = arg3;
        }
        const call = this.values[key];
        const newCall = call === undefined ? this.addMissing({ callType }) : Object.assign({}, call);
        if (callType !== undefined)
            newCall.callType = callType;
        this.initializeCall(newCall, callId, 'outgoing', 'ringing', extras);
        if ((call === null || call === void 0 ? void 0 : call.callId) !== undefined && call.callId !== newCall.callId) {
            // If there was an existing call for this key with a different callId, then disconnect
            // it. This is a bug if it happens, but we handle this case so we don't get a second,
            // stuck call instance.
            this.disconnect(key);
        }
        this.log.debug(`call: ${key} = ${JSON.stringify(newCall, null, 4)}`);
        this.update(key, newCall);
    }
    // Connect a call. This should be used in the following circumstances.
    //
    // 1. To transition a "ringing" call to "connected" when the appropriate status message is
    //    received from the device.
    //
    // 2. When a status message from the device inidicates that a call is connected. This can
    //    happen if Inara starts up when the device is already in a call or if a third party
    //    controller places or answers a call.
    //
    // 3. Instead of the call method for call interfaces (such as most PSTN interfaces) that do not
    //    support a status message for outgoing calls to transition from "ringing" to "connected."
    connect(key, extras, callId, callType) {
        var _a;
        const call = (_a = this.values[key]) !== null && _a !== void 0 ? _a : (callType ? this.addMissing({ callType }) : undefined);
        if (callType !== undefined)
            call.callType = callType;
        let newCall;
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnected') {
            newCall = Object.assign({}, call); // shallow copy is sufficient for this change
            this.initializeCall(newCall, callId, 'outgoing', 'connected', extras);
        }
        else if (call.callState === 'ringing') {
            newCall = Object.assign(Object.assign({}, call), { callState: 'connected' }); // shallow copy is sufficient for this
            this.internalUpdateCall(newCall, extras, newCall);
        }
        else if (call.callState !== 'connected') {
            this.log.error(`connecting call with invalid callState = ${call.callState}`);
            newCall = Object.assign(Object.assign({}, call), { callState: 'connected' }); // shallow copy is sufficient for this
            this.internalUpdateCall(newCall, extras, newCall);
        }
        else {
            newCall = this.internalUpdateCall(call, extras);
        }
        if (newCall !== undefined) {
            this.log.debug(`connect: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    // Dial additional digits while a call is connected.
    dial(key, dialString) {
        const call = this.values[key];
        const newCall = Object.assign({}, call); // shallow copy is sufficient for this change
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (newCall.extraDialString === undefined) {
            newCall.extraDialString = dialString;
        }
        else {
            newCall.extraDialString += dialString;
        }
        this.update(key, newCall);
    }
    disconnect(key, arg1, arg2) {
        var _a;
        let callType;
        let extras;
        if (typeof arg1 === 'string' && (arg2 === undefined || typeof arg2 !== 'string')) {
            callType = arg1;
            extras = arg2;
        }
        else if (typeof arg1 !== 'string' && (arg2 === undefined || typeof arg2 === 'string')) {
            extras = arg1;
            callType = arg2;
        }
        const call = (_a = this.values[key]) !== null && _a !== void 0 ? _a : (callType ? this.addMissing({ callType }) : undefined);
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnected')
            return;
        let newCall = Object.assign({}, call); // shallow copy is sufficient for these changes
        if (call.callState === 'ringing')
            newCall.missed = true;
        newCall.callState = 'disconnected';
        if (call.end === undefined)
            newCall.end = Date.now();
        if (extras)
            newCall = this.internalUpdateCall(newCall, extras);
        this.log.debug(`disconnect: ${key} = ${JSON.stringify(newCall, null, 4)}`);
        this.update(key, newCall);
    }
    // End a call. This should be used both for rejecting an incoming call and hanging up an
    // outgoing call.
    end(key) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnecting' || call.callState === 'disconnected') {
            return;
        }
        // shallow copy is sufficient for these changes
        const newCall = Object.assign(Object.assign({}, call), { callState: 'disconnecting', end: Date.now() });
        if (call.callState === 'connected' || call.callState === 'ringing') {
            if (call.callState === 'ringing')
                newCall.missed = true;
        }
        else {
            this.log.error(`ending call with invalid callState = ${call.callState}`);
        }
        this.log.debug(`end: ${key} = ${JSON.stringify(newCall, null, 4)}`);
        this.update(key, newCall);
    }
    // Remove a call.
    remove(key) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState !== 'disconnected') {
            this.log.error(`removing call in ${call.callState} state, key = ${key}`);
            this.disconnect(key);
        }
        // notifyListeners doesn't support undefined value, so we can't notify listeners that the
        // call was removed.
        // this.notifyListeners(key, undefined);
        this.log.debug(`remove: ${key}`);
        delete this.values[key];
    }
    sendVideo(key, value) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState !== 'connected') {
            this.log.error(`sending video on call in ${call.callState} state, key = ${key}`);
        }
        if (call.sendingVideo === value)
            return;
        // shallow copy is sufficient for this change
        const newCall = Object.assign(Object.assign({}, call), { sendingVideo: value });
        this.log.debug(`sendVideo: ${key} = ${JSON.stringify(newCall, null, 4)}`);
        this.update(key, newCall);
    }
    setIncoming(key, arg1, arg2) {
        var _a;
        let callType;
        let extras;
        if (typeof arg1 === 'string') {
            callType = arg1;
        }
        else if (typeof arg2 === 'string') {
            callType = arg2;
        }
        if (arg1 !== undefined && typeof arg1 !== 'string') {
            extras = arg1;
        }
        else if (arg2 !== undefined && typeof arg2 !== 'string') {
            extras = arg2;
        }
        const call = (_a = this.values[key]) !== null && _a !== void 0 ? _a : (callType ? this.addMissing({ callType }) : undefined);
        let newCall;
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnected') {
            newCall = Object.assign({}, call); // shallow copy is sufficient for this change
            newCall = this.initializeCall(newCall, undefined, 'incoming', 'ringing', extras);
        }
        else if (call.callState !== 'ringing') {
            this.log.warning(`changing to incoming on call in ${call.callState} state, key = ${key}`);
            newCall = Object.assign(Object.assign({}, call), { callState: 'ringing' }); // shallow copy is sufficient for this change
        }
        if (newCall !== undefined) {
            this.log.debug(`incoming: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    // Set a call to the "ringing" state for an outgoing call. This should be used by the driver
    // when it receives a status message indicating there is an outgoing call ringing due to the
    // call being placed by a third party controller.
    setOutgoing(key, callType, extras, callId) {
        var _a;
        const call = (_a = this.values[key]) !== null && _a !== void 0 ? _a : (callType ? this.addMissing({ callType }) : undefined);
        let newCall;
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.callState === 'disconnected') {
            newCall = Object.assign({}, call); // shallow copy is sufficient for this change
            this.initializeCall(newCall, callId, 'outgoing', 'ringing', extras);
        }
        else if (call.callState !== 'ringing') {
            this.log.warning(`changing to outgoing on call in ${call.callState} state, key = ${key}`);
            newCall = Object.assign(Object.assign({}, call), { callState: 'ringing' }); // shallow copy is sufficient for this
        }
        if (newCall !== undefined) {
            this.log.debug(`outgoing: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    updateCall(key, extras, notify = true) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        const newCall = this.internalUpdateCall(call, extras);
        if (newCall !== undefined) {
            this.log.debug(`updateCall: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    updateCallExtendedState(key, callExtendedState) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        const newCall = Object.assign({}, call); // shallow copy is sufficient for this change
        if (callExtendedState === undefined) {
            delete newCall.callExtendedState;
        }
        else {
            newCall.callExtendedState = callExtendedState;
        }
        this.log.debug(`updateCallExtendedState: ${key} = ${JSON.stringify(newCall, null, 4)}`);
        this.update(key, newCall);
    }
    updateExisting(key, update) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        this.log.debug(`updateExisting: ${key} = ${JSON.stringify(update, null, 4)}`);
        this.update(key, update);
    }
    updateLayout(key, value) {
        var _a;
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (value !== ((_a = call.layouts) === null || _a === void 0 ? void 0 : _a.current)) {
            const newCall = Object.assign({}, call); // shallow copy here, but deep copy layouts
            if (call.layouts)
                newCall.layouts = Sutl.deepCopy(call.layouts);
            Sutl.setProp(newCall, 'layouts.current', value);
            this.log.debug(`updateLayout: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    updateLayouts(key, layouts) {
        var _a, _b;
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (!Sutl.objEquals(layouts, (_a = call === null || call === void 0 ? void 0 : call.layouts) === null || _a === void 0 ? void 0 : _a.available)) {
            const newCall = Object.assign({}, call); // shallow copy here, but deep copy layouts
            if (call.layouts)
                newCall.layouts = Sutl.deepCopy(call.layouts);
            Sutl.setProp(newCall, 'layouts.available', layouts);
            if (((_b = newCall.layouts) === null || _b === void 0 ? void 0 : _b.current) === undefined) {
                Sutl.setProp(newCall, 'layouts.current', Object.keys(layouts).sort()[0]);
            }
            this.log.debug(`updateLayouts: ${key} = ${JSON.stringify(newCall, null, 4)}`);
            this.update(key, newCall);
        }
    }
    updateReceiveGain(key, value) {
        const call = this.values[key];
        if (call === undefined) {
            this.log.error(`no call with key = ${key}`);
            return;
        }
        if (call.receiveGain === value)
            return;
        this.update(key, Object.assign(Object.assign({}, call), { receiveGain: value })); // shallow copy is sufficient for this
    }
    // --------------------------------------------------------------------------------------------
    // Methods for framework to manage the state.
    // --------------------------------------------------------------------------------------------
    destroy() {
        for (const key of Object.keys(this.values)) {
            const call = this.values[key];
            if (call.callState !== 'disconnected')
                this.disconnect(key);
        }
        this.clear();
    }
    getKeyByCallId(callId) {
        for (const key of Object.keys(this.values)) {
            const call = this.values[key];
            if (call.callId === callId)
                return key;
        }
        return undefined;
    }
    getMaxCalls() {
        var _a;
        const callCounts = {};
        callTypes.forEach(callType => (callCounts[callType] = 0));
        for (const call of Object.values(this.values)) {
            callCounts[call.callType] = ((_a = callCounts[call.callType]) !== null && _a !== void 0 ? _a : 0) + 1;
        }
        return callCounts;
    }
    getNumActiveCalls(callType) {
        let numCalls = 0;
        for (const call of Object.values(this.values)) {
            if (call.callType === callType && call.callState !== 'disconnected') {
                numCalls++;
            }
        }
        return numCalls;
    }
    isInCall(types) {
        const _types = types === undefined ? callTypes : typeof types === 'string' ? [types] : types;
        for (const call of Object.values(this.values)) {
            if (_types.includes(call.callType) && call.callState === 'connected') {
                return true;
            }
        }
        return false;
    }
    // --------------------------------------------------------------------------------------------
    // Private methods
    // --------------------------------------------------------------------------------------------
    initializeCall(call, callId, direction, callState, extras) {
        call.callId = callId === undefined ? Sutl.generateFirebaseId() : callId;
        call.direction = direction;
        call.start = Date.now();
        call.callState = callState;
        delete call.end;
        delete call.extraDialString;
        delete call.meetingCodes;
        delete call.missed;
        this.callParticipants.clear(call.callId);
        if ((extras === null || extras === void 0 ? void 0 : extras.callExtendedState) === undefined) {
            delete call.callExtendedState;
        }
        else {
            call.callExtendedState = extras.callExtendedState;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.callInListId) === undefined) {
            delete call.callInListId;
        }
        else {
            call.callInListId = extras.callInListId;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.contactName) === undefined) {
            delete call.contactName;
        }
        else {
            call.contactName = extras.contactName;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.defaultCallInCountryId) === undefined) {
            delete call.defaultCallInCountryId;
        }
        else {
            call.defaultCallInCountryId = extras.defaultCallInCountryId;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.dialString) === undefined) {
            delete call.dialString;
        }
        else {
            call.dialString = extras.dialString;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.displayDialString) === undefined) {
            delete call.displayDialString;
        }
        else {
            call.displayDialString = extras.displayDialString;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.meetingCodes) !== undefined) {
            call.meetingCodes = [...extras.meetingCodes]; // shallow copy is sufficient
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.meetingPassword) === undefined) {
            delete call.meetingPassword;
        }
        else {
            call.meetingPassword = extras.meetingPassword;
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.participantId) === undefined) {
            delete call.participantId;
        }
        else {
            call.participantId = extras.participantId;
        }
        if (extras === undefined) {
            delete call.sendingContent;
            delete call.sendingVideo;
            delete call.layouts;
        }
        else {
            if ('canDial' in extras) {
                if (extras.canDial === undefined) {
                    delete call.canDial;
                }
                else {
                    call.canDial = extras.canDial;
                }
            }
            if ('canEndMeeting' in extras) {
                if (extras.canEndMeeting === undefined) {
                    delete call.canEndMeeting;
                }
                else {
                    call.canEndMeeting = extras.canEndMeeting;
                }
            }
            if ('canInvite' in extras) {
                if (extras.canInvite === undefined) {
                    delete call.canInvite;
                }
                else {
                    call.canInvite = extras.canInvite;
                }
            }
            if ('canRecordMeeting' in extras) {
                if (extras.canRecordMeeting === undefined) {
                    delete call.canRecordMeeting;
                }
                else {
                    call.canRecordMeeting = extras.canRecordMeeting;
                }
            }
            if ('isBeingRecorded' in extras) {
                if (extras.isBeingRecorded === undefined) {
                    delete call.isBeingRecorded;
                }
                else {
                    call.isBeingRecorded = extras.isBeingRecorded;
                }
            }
            if ('layouts' in extras) {
                if (extras.layouts === undefined) {
                    delete call.layouts;
                }
                else {
                    call.layouts = extras.layouts;
                }
            }
            if ('sendingContent' in extras) {
                if (extras.sendingContent === undefined) {
                    delete call.sendingContent;
                }
                else {
                    call.sendingContent = extras.sendingContent;
                }
            }
            if ('sendingVideo' in extras) {
                if (extras.sendingVideo === undefined) {
                    delete call.sendingVideo;
                }
                else {
                    call.sendingVideo = extras.sendingVideo;
                }
            }
        }
        return call;
    }
    internalUpdateCall(call, extras, newCall = undefined) {
        // shallow copies are sufficient for these changes
        if ((extras === null || extras === void 0 ? void 0 : extras.callExtendedState) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.callExtendedState === null) {
                delete newCall.callExtendedState;
            }
            else {
                newCall.callExtendedState = extras.callExtendedState;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.callInListId) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.callInListId === null) {
                delete newCall.callInListId;
            }
            else {
                newCall.callInListId = extras.callInListId;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.canDial) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.canDial === null) {
                delete newCall.canDial;
            }
            else {
                newCall.canDial = extras.canDial;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.canEndMeeting) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.canEndMeeting === null) {
                delete newCall.canEndMeeting;
            }
            else {
                newCall.canEndMeeting = extras.canEndMeeting;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.canInvite) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.canInvite === null) {
                delete newCall.canInvite;
            }
            else {
                newCall.canInvite = extras.canInvite;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.canRecordMeeting) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.canRecordMeeting === null) {
                delete newCall.canRecordMeeting;
            }
            else {
                newCall.canRecordMeeting = extras.canRecordMeeting;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.contactName) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.contactName === null) {
                delete newCall.contactName;
            }
            else {
                newCall.contactName = extras.contactName;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.defaultCallInCountryId) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.defaultCallInCountryId === null) {
                delete newCall.defaultCallInCountryId;
            }
            else {
                newCall.defaultCallInCountryId = extras.defaultCallInCountryId;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.dialString) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.dialString === null) {
                delete newCall.dialString;
            }
            else {
                newCall.dialString = extras.dialString;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.displayDialString) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.displayDialString === null) {
                delete newCall.displayDialString;
            }
            else {
                newCall.displayDialString = extras.displayDialString;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.isBeingRecorded) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.isBeingRecorded === null) {
                delete newCall.isBeingRecorded;
            }
            else {
                newCall.isBeingRecorded = extras.isBeingRecorded;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.layouts) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.layouts === null) {
                delete newCall.layouts;
            }
            else {
                newCall.layouts = extras.layouts;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.meetingCodes) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.meetingCodes === null) {
                delete newCall.meetingCodes;
            }
            else {
                newCall.meetingCodes = [...extras.meetingCodes]; // shallow copy is sufficient
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.meetingPassword) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.meetingPassword === null) {
                delete newCall.meetingPassword;
            }
            else {
                newCall.meetingPassword = extras.meetingPassword;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.participantId) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.participantId === null) {
                delete newCall.participantId;
            }
            else {
                newCall.participantId = extras.participantId;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.sendingContent) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.sendingContent === null) {
                delete newCall.sendingContent;
            }
            else {
                newCall.sendingContent = extras.sendingContent;
            }
        }
        if ((extras === null || extras === void 0 ? void 0 : extras.sendingVideo) !== undefined) {
            if (newCall === undefined)
                newCall = Object.assign({}, call);
            if (extras.sendingVideo === null) {
                delete newCall.sendingVideo;
            }
            else {
                newCall.sendingVideo = extras.sendingVideo;
            }
        }
        return newCall !== null && newCall !== void 0 ? newCall : call;
    }
}
